import type { SoldCostsCovered, SoldCostsDetailMonth, SoldCostsHeld, SoldCostsLeaf } from "./api";
import { monthsBetween } from "./format";
import { heldFor } from "./monthState";

export type SoldCostsMode = "month" | "trailing_3";
export type LeafRow = {
  nodeId: string;
  label: string;
  subRow: boolean;
  perUnit: Record<string, number | null>;
  perUnitTrailing3: Record<string, number | null>;
  expected: Record<string, number | null>;
  coveredStatus: Record<string, SoldCostsCovered["status"] | undefined>;
};
export type LeafBlock = { group: string; label: string; covered: boolean; rows: LeafRow[] };
export type ChartSeries = { key: string; name: string; total: boolean };
export type ExpectedWindow = { month: string; window: string[]; consecutive: boolean } | null;

// C4 v1.6.4 (Bojan, 1 Nov 2026): Expected (trailing 3) weights the newest used month 50%, the one before 30%, the oldest 20%.
// Fewer than three used months: the first weights in that order, rescaled to sum to 1 (two months 0.625 / 0.375, one month 1).
export const TRAILING_WEIGHTS = [0.5, 0.3, 0.2] as const;

// C4 v1.6.5: one entry per month of the tab's period. A month the engine served nothing for gets a placeholder in the same
// shape as the held placeholder in assembleSoldCostsMonths (closed: false), so it enters no trailing window and counts in no
// sum; noData names those months so the drawer can mark them. Served and held months are returned as given.
export function padSoldCostsMonths(months: SoldCostsDetailMonth[], from: string, to: string): { months: SoldCostsDetailMonth[]; noData: ReadonlySet<string> } {
  const byMonth = new Map(months.map((month) => [month.month.slice(0, 7), month] as const));
  const period = monthsBetween(from, to);
  const missing = period.filter((month) => !byMonth.has(month));
  if (missing.length === 0) return { months, noData: new Set<string>() };
  const padded = period.map((month): SoldCostsDetailMonth => byMonth.get(month) ?? {
    month, units: "", amount: "", per_unit: null, per_unit_trailing_3: null,
    closed: false, version: 0, leaves: [], covered: [],
  });
  return { months: padded, noData: new Set(missing) };
}

// used: "YYYY-MM" months in ascending order (every caller passes window months, which are ascending). Returns month -> weight,
// keys newest first. No sorting call: the C5 v2.6 h8 / v2.8 h6 source tests forbid one in historyFeesModel.ts.
export function trailingWeights(used: string[]): Record<string, number> {
  const newestFirst = used.slice(-3).reverse();
  const raw = newestFirst.map((_, index) => TRAILING_WEIGHTS[index] ?? 0);
  const sum = raw.reduce((total, weight) => total + weight, 0);
  return Object.fromEntries(newestFirst.map((month, index) => [month, (raw[index] ?? 0) / sum]));
}

// "weighted 50/30/20, newest first" (3 months), "weighted 62.5/37.5, newest first" (2), null (0 or 1).
export function weightLabel(used: string[]): string | null {
  if (used.length < 2) return null;
  return `weighted ${Object.values(trailingWeights(used)).map((weight) => Number((weight * 100).toFixed(1))).join("/")}, newest first`;
}

function eligible(month: SoldCostsDetailMonth, held: SoldCostsHeld[] | undefined, entityId: string): boolean {
  return month.closed === true && month.history !== true && !heldFor(held, entityId, month.month);
}

function nextMonth(month: string): string {
  const [year, mm] = month.slice(0, 7).split("-").map(Number);
  return mm === 12 ? `${year + 1}-01` : `${year}-${String(mm + 1).padStart(2, "0")}`;
}

export function expectedWindow(months: SoldCostsDetailMonth[], held: SoldCostsHeld[] | undefined, entityId: string): ExpectedWindow {
  const closed = months.filter((month) => eligible(month, held, entityId)).map((month) => month.month.slice(0, 7));
  if (closed.length === 0) return null;
  const window = closed.slice(-3);
  const consecutive = window.length === 3 && nextMonth(window[0]) === window[1] && nextMonth(window[1]) === window[2];
  return { month: window[window.length - 1], window, consecutive };
}

// C4 v1.6.4: window months with units sold, ascending.
export function usedMonths(months: SoldCostsDetailMonth[], w: NonNullable<ExpectedWindow>): string[] {
  return w.window.filter((window) => months.some((month) => month.month.slice(0, 7) === window && Number(month.units) > 0));
}

// The row's 50/30/20 value over the used months; a used month with no cost for the row counts as zero.
// null when no used month has a value for the row, or no window month has units.
export function weightedTrailing(row: LeafRow, months: SoldCostsDetailMonth[], w: NonNullable<ExpectedWindow>): number | null {
  const used = months.filter((month) => w.window.includes(month.month.slice(0, 7)) && Number(month.units) > 0);
  const cells = used.map((month) => row.perUnit[month.month]);
  if (!cells.some((value) => value !== null && value !== undefined)) return null;
  const weights = trailingWeights(used.map((month) => month.month.slice(0, 7)));
  return used.reduce((sum, month, index) => sum + (weights[month.month.slice(0, 7)] ?? 0) * (cells[index] ?? 0), 0);
}

// The trailing window of each column, in month order: the window ending at that month when the month is eligible, else null.
export function trailingWindows(months: SoldCostsDetailMonth[], held: SoldCostsHeld[] | undefined, entityId: string): ExpectedWindow[] {
  return months.map((month, index) => {
    const w = expectedWindow(months.slice(0, index + 1), held, entityId);
    return w && w.month === month.month.slice(0, 7) ? w : null;
  });
}

// Copies of the blocks whose spread rows carry the weighted value in perUnitTrailing3 for every month in view
// (null where the month is not eligible). Covered blocks are returned as they are. Idempotent: it reads only perUnit.
export function weightedBlocks(blocks: LeafBlock[], months: SoldCostsDetailMonth[], held: SoldCostsHeld[] | undefined, entityId: string): LeafBlock[] {
  const windows = trailingWindows(months, held, entityId);
  return blocks.map((block) => block.covered ? block : {
    ...block,
    rows: block.rows.map((row) => ({
      ...row,
      perUnitTrailing3: Object.fromEntries(months.map((month, index) => {
        const w = windows[index];
        return [month.month, w ? weightedTrailing(row, months, w) : null];
      })),
    })),
  });
}

export function sparseMonths(row: LeafRow, window: NonNullable<ExpectedWindow>): string[] {
  if (!window.consecutive) return [];
  return window.window.filter((month) => !(month in row.perUnit));
}

function rowFor(block: LeafBlock, nodeId: string, label: string, subRow = false): LeafRow {
  let row = block.rows.find((candidate) => candidate.nodeId === nodeId);
  if (!row) {
    row = { nodeId, label, subRow, perUnit: {}, perUnitTrailing3: {}, expected: {}, coveredStatus: {} };
    block.rows.push(row);
  }
  return row;
}

function blockFor(blocks: LeafBlock[], group: string, label: string, covered: boolean): LeafBlock {
  let block = blocks.find((candidate) => candidate.group === group && candidate.covered === covered);
  if (!block) {
    block = { group, label, covered, rows: [] };
    blocks.push(block);
  }
  return block;
}

function assignLeaf(row: LeafRow, month: string, leaf: SoldCostsLeaf): void {
  row.perUnit[month] = leaf.per_unit === null ? null : Number(leaf.per_unit);
  row.perUnitTrailing3[month] = leaf.per_unit_trailing_3 === null ? null : Number(leaf.per_unit_trailing_3);
  row.expected[month] = leaf.value_expected === null ? null : Number(leaf.value_expected);
}

export function leafBlocks(months: SoldCostsDetailMonth[]): LeafBlock[] {
  const blocks: LeafBlock[] = [];
  for (const month of months) {
    for (const leaf of month.leaves) {
      const row = rowFor(blockFor(blocks, leaf.ofs_group, leaf.ofs_group_label, false), leaf.node_id, leaf.leaf_label, leaf.parent_code !== undefined);
      assignLeaf(row, month.month, leaf);
    }
  }
  for (const month of months) {
    for (const covered of month.covered) {
      const row = rowFor(blockFor(blocks, covered.ofs_group, covered.ofs_group_label, true), covered.node_id, covered.leaf_label);
      row.coveredStatus[month.month] = covered.status;
    }
  }
  return blocks;
}

function valuesFor(block: LeafBlock, month: string, mode: SoldCostsMode): Array<number | null | undefined> {
  const key = mode === "month" ? "perUnit" : "perUnitTrailing3";
  return block.rows.filter((row) => !row.subRow).map((row) => row[key][month]);
}

export function blockSubtotal(block: LeafBlock, month: string, mode: SoldCostsMode = "month"): number | null {
  if (block.covered) return null;
  const present = valuesFor(block, month, mode).filter((value): value is number => value !== null && value !== undefined);
  return present.length ? present.reduce((sum, value) => sum + value, 0) : null;
}

export function totalPerUnit(blocks: LeafBlock[], month: string, mode: SoldCostsMode = "month"): number | null {
  const values = blocks.filter((block) => !block.covered).map((block) => blockSubtotal(block, month, mode)).filter((value): value is number => value !== null);
  return values.length ? values.reduce((sum, value) => sum + value, 0) : null;
}

export function expectedTotal(blocks: LeafBlock[], month?: string): number | null {
  const rows = blocks.filter((block) => !block.covered).flatMap((block) => block.rows.filter((row) => !row.subRow));
  const selectedMonth = month ?? Object.keys(rows[0]?.expected ?? {})[0];
  if (!selectedMonth) return null;
  const values = rows.filter((row) => selectedMonth in row.expected).map((row) => row.expected[selectedMonth]);
  if (values.length === 0 || values.some((value) => value === null || value === undefined)) return null;
  return (values as number[]).reduce((sum, value) => sum + value, 0);
}

export function chartSeries(blocks: LeafBlock[]): ChartSeries[] {
  return [
    ...blocks.filter((block) => !block.covered).map((block) => ({ key: `block:${block.group}`, name: block.label, total: false })),
    { key: "total", name: "Total Sold-COGS per unit", total: true },
  ];
}

export function chartRows(blocks: LeafBlock[], months: SoldCostsDetailMonth[], mode: SoldCostsMode = "month"): Array<Record<string, string | number | null>> {
  return months.map((month) => ({
    month: month.month,
    ...Object.fromEntries(blocks.filter((block) => !block.covered).map((block) => [`block:${block.group}`, blockSubtotal(block, month.month, mode)])),
    total: totalPerUnit(blocks, month.month, mode),
  }));
}

// C4 v1.6.3: the drawer chart splits each spread block into its costs (leaves above zero) and its credits (leaves below zero),
// so a credit draws below the axis in its block's colour. Costs + credits = blockSubtotal for the same month and mode.
export function signedChartRows(blocks: LeafBlock[], months: SoldCostsDetailMonth[], mode: SoldCostsMode = "month"): Array<Record<string, string | number | null>> {
  return months.map((month) => {
    const row: Record<string, string | number | null> = { month: month.month };
    for (const block of blocks.filter((candidate) => !candidate.covered)) {
      const values = valuesFor(block, month.month, mode).filter((value): value is number => value !== null && value !== undefined);
      const costs = values.filter((value) => value > 0);
      const credits = values.filter((value) => value < 0);
      row[`block:${block.group}:costs`] = costs.length ? costs.reduce((sum, value) => sum + value, 0) : null;
      row[`block:${block.group}:credits`] = credits.length ? credits.reduce((sum, value) => sum + value, 0) : null;
    }
    return row;
  });
}

// C4 v1.6.3: one extra chart row, the Expected (trailing 3) value at the drawer's trailing month, split into costs and credits
// like the month rows. Costs + credits = blockSubtotal(block, w.month, "trailing_3"). null when there is no window.
export function expectedChartRow(blocks: LeafBlock[], w: ExpectedWindow): Record<string, string | number | null> | null {
  if (!w) return null;
  const row: Record<string, string | number | null> = { month: "Expected (trailing 3)", expected: 1 };
  for (const block of blocks.filter((candidate) => !candidate.covered)) {
    const values = valuesFor(block, w.month, "trailing_3").filter((value): value is number => value !== null && value !== undefined);
    const costs = values.filter((value) => value > 0);
    const credits = values.filter((value) => value < 0);
    row[`block:${block.group}:costs`] = costs.length ? costs.reduce((sum, value) => sum + value, 0) : null;
    row[`block:${block.group}:credits`] = credits.length ? credits.reduce((sum, value) => sum + value, 0) : null;
  }
  return row;
}
